import { useEffect, useRef, useState } from 'react';

const DEFAULT_DEBOUNCE_MS = 1000;

export interface UseSearchKeywordOptions {
    /** Controlled keyword from {@link useBrowseQuery} / URL. */
    keyword: string;
    /** Called after debounce when the user stops typing. */
    onDebouncedKeywordChange: (keyword: string) => void;
    debounceMs?: number;
}

export interface UseSearchKeywordResult {
    inputValue: string;
    setInputValue: (value: string) => void;
}

/**
 * Debounced keyword input hook.
 * Mirrors Angular `ng-model-options="{ debounce: 1000 }"` on
 * `#item-search-input` in `search-filter.tpl.html`.
 */
export function useSearchKeyword({
    keyword,
    onDebouncedKeywordChange,
    debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseSearchKeywordOptions): UseSearchKeywordResult {
    const [inputValue, setInputValue] = useState(keyword);
    const onDebouncedKeywordChangeRef = useRef(onDebouncedKeywordChange);

    useEffect(() => {
        onDebouncedKeywordChangeRef.current = onDebouncedKeywordChange;
    }, [onDebouncedKeywordChange]);

    // Deep links / back-forward navigation repopulate the input immediately.
    useEffect(() => {
        setInputValue(keyword);
    }, [keyword]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            onDebouncedKeywordChangeRef.current(inputValue);
        }, debounceMs);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [inputValue, debounceMs]);

    return { inputValue, setInputValue };
}
